import React from 'react';
import { Link } from 'react-router-dom';
import { QRCodeSVG } from 'qrcode.react';
import { ExternalLink } from 'lucide-react';
import { TIER } from '../../utils/tier-constants';
import { PLANS, Tier } from '../../config/plans';
import { Banner, Button, Dialog, Field, Input, Section, buttonClasses } from '../../components/ui';

export interface AuthUserLike {
  email?: string;
  telegramId?: number;
  tier: string;
  premiumExpiresAt?: number;
  createdAt: number;
}

export interface ProfileSectionProps {
  user: AuthUserLike | null;
  isLoading: boolean;
  email: string;
  onEmailChange: (value: string) => void;
  onUpdateProfile: (e: React.FormEvent) => void;
  addEmail: string;
  onAddEmailChange: (value: string) => void;
  addEmailPassword: string;
  onAddEmailPasswordChange: (value: string) => void;
  onAddEmail: (e: React.FormEvent) => void;
  telegramDeepLink: string;
  isGeneratingLink: boolean;
  onGenerateTelegramLink: () => void;
  showMergeDialog: boolean;
  onCloseMergeDialog: () => void;
  mergeEmail: string;
  mergePassword: string;
  onMergePasswordChange: (value: string) => void;
  onMerge: (e: React.FormEvent) => void;
}

export const ProfileSection: React.FC<ProfileSectionProps> = ({
  user,
  isLoading,
  email,
  onEmailChange,
  onUpdateProfile,
  addEmail,
  onAddEmailChange,
  addEmailPassword,
  onAddEmailPasswordChange,
  onAddEmail,
  telegramDeepLink,
  isGeneratingLink,
  onGenerateTelegramLink,
  showMergeDialog,
  onCloseMergeDialog,
  mergeEmail,
  mergePassword,
  onMergePasswordChange,
  onMerge,
}) => {
  const tier = (user?.tier ?? TIER.FREE).toLowerCase() as Tier;
  const planName = PLANS[tier]?.name ?? PLANS.free.name;
  const canUpgrade = tier === TIER.FREE || tier === TIER.PRO;

  return (
    <div className="flex flex-col">
      <Section title="E-Mail">
        {user?.email ? (
          <form onSubmit={onUpdateProfile} className="flex flex-col gap-3">
            <Field
              label="E-Mail-Adresse"
              htmlFor="email"
              hint="Gib eine neue Adresse ein, um sie zu ändern."
            >
              <Input
                type="email"
                id="email"
                value={email}
                onChange={(e) => onEmailChange(e.target.value)}
                required
                disabled={isLoading}
                aria-describedby="email-hint"
              />
            </Field>
            <Button type="submit" disabled={isLoading || email === user?.email} loading={isLoading} className="self-start">
              E-Mail ändern
            </Button>
          </form>
        ) : (
          <form onSubmit={onAddEmail} className="flex flex-col gap-3">
            <Banner tone="info">
              Füge eine E-Mail-Adresse hinzu, um dich auch per E-Mail einzuloggen und E-Mail-Benachrichtigungen zu erhalten.
            </Banner>
            <Field label="E-Mail-Adresse" htmlFor="addEmail">
              <Input
                type="email"
                id="addEmail"
                value={addEmail}
                onChange={(e) => onAddEmailChange(e.target.value)}
                required
                disabled={isLoading}
                placeholder="deine@email.de"
              />
            </Field>
            <Field label="Passwort festlegen" htmlFor="addEmailPassword" hint="Dieses Passwort benötigst du für den E-Mail-Login.">
              <Input
                type="password"
                id="addEmailPassword"
                value={addEmailPassword}
                onChange={(e) => onAddEmailPasswordChange(e.target.value)}
                required
                minLength={8}
                disabled={isLoading}
                placeholder="Mindestens 8 Zeichen"
                aria-describedby="addEmailPassword-hint"
              />
            </Field>
            <Button type="submit" disabled={isLoading} loading={isLoading} className="self-start">
              E-Mail hinzufügen
            </Button>
          </form>
        )}
      </Section>

      <Section title="Telegram">
        {user?.telegramId ? (
          <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 text-sm">
            <dt className="text-muted">Verknüpfung</dt>
            <dd className="text-ink">Verknüpft mit @schnellerwohnen_bot (ID: {user.telegramId})</dd>
          </dl>
        ) : telegramDeepLink ? (
          <div className="flex flex-wrap items-center gap-6">
            <QRCodeSVG value={telegramDeepLink} size={140} level="M" />
            <div>
              <p className="mb-2 text-sm text-ink">Scanne den QR-Code oder öffne den Link:</p>
              <a
                href={telegramDeepLink}
                target="_blank"
                rel="noopener noreferrer"
                className={buttonClasses('primary', 'sm')}
              >
                Bot öffnen
              </a>
              <p className="mt-2 text-xs text-muted">Der Link ist 15 Minuten gültig.</p>
            </div>
          </div>
        ) : (
          <div className="flex flex-col gap-2">
            <p className="text-sm text-muted">
              Verknüpfe deinen Account mit Telegram, um Meldungen über neue Wohnungsangebote direkt auf dein Handy zu
              erhalten.
            </p>
            <Button
              type="button"
              variant="secondary"
              onClick={onGenerateTelegramLink}
              disabled={isGeneratingLink}
              loading={isGeneratingLink}
              className="self-start"
            >
              Telegram verbinden
            </Button>
          </div>
        )}
      </Section>

      <Section title="Konto">
        <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-2 text-sm">
          <dt className="text-muted">Nutzer seit</dt>
          <dd className="text-ink">{user?.createdAt ? new Date(user.createdAt).toLocaleDateString('de-DE') : '—'}</dd>

          <dt className="text-muted">Plan</dt>
          <dd className="flex flex-wrap items-center gap-2 text-ink">
            {planName}
            {canUpgrade && (
              <Link to="/dashboard/upgrade" className="inline-flex items-center gap-1 font-medium text-brand hover:underline">
                Upgrade ansehen
                <ExternalLink className="h-3.5 w-3.5" aria-hidden="true" />
              </Link>
            )}
            {user?.premiumExpiresAt && (tier === TIER.PRO || tier === TIER.PREMIUM) && (
              <span className="text-muted">
                gültig bis {new Date(user.premiumExpiresAt).toLocaleDateString('de-DE')}
              </span>
            )}
          </dd>
        </dl>
      </Section>

      <Dialog
        open={showMergeDialog}
        onClose={onCloseMergeDialog}
        title="Accounts zusammenführen"
        description={
          <>
            Die E-Mail-Adresse <strong>{mergeEmail}</strong> ist bereits registriert. Möchtest du die Accounts
            zusammenführen?
          </>
        }
        footer={
          <>
            <Button type="button" variant="ghost" onClick={onCloseMergeDialog} disabled={isLoading}>
              Abbrechen
            </Button>
            <Button type="submit" form="merge-accounts-form" disabled={isLoading} loading={isLoading}>
              Accounts zusammenführen
            </Button>
          </>
        }
      >
        <ul className="mb-4 flex flex-col gap-1 text-sm text-ink">
          <li>Alle Suchen beider Accounts werden übernommen.</li>
          <li>Login über E-Mail und Telegram ist danach möglich.</li>
          <li>Der höchste Premium-Status gilt weiter.</li>
        </ul>
        <form id="merge-accounts-form" onSubmit={onMerge}>
          <Field label="Passwort des E-Mail-Accounts" htmlFor="mergePassword">
            <Input
              type="password"
              id="mergePassword"
              value={mergePassword}
              onChange={(e) => onMergePasswordChange(e.target.value)}
              required
              disabled={isLoading}
              placeholder="Passwort eingeben"
            />
          </Field>
        </form>
      </Dialog>
    </div>
  );
};
